import { Injectable } from '@angular/core';
import { Audience } from '../config/audience.enum';
import { TourStateStatus } from '../config/tour-state.enum';
import { TourTrigger } from '../config/trigger.enum';
import { UserMaturity } from '../config/user-maturity.enum';
import { TourConfig } from '../models/tour-config.model';
import { TourStateSnapshot } from '../models/tour-state-snapshot.model';
import { UserPermissionConfig } from 'src/app/modules/employee/model/user-permissions.model';
import { hasAllPermissions } from './permission-gate';

export type EligibilityReason =
  | 'kill-switch'
  | 'audience-mismatch'
  | 'permission-mismatch'
  | 'maturity-mismatch'
  | 'route-mismatch'
  | 'event-mismatch'
  | 'state-block'
  | 'offer-cooldown'
  | 'snooze-active'
  | 'dismiss-cooldown'
  | 'trigger-mismatch'
  | 'resume-nudge-cooldown'
  | 'max-views-reached';

export type EligibilityResult =
  | { eligible: true }
  | { eligible: false; reason: EligibilityReason; nextEligibleAt?: Date };

export interface EligibilityContext {
  tour: TourConfig;
  audience: Audience[];
  /** Aktueller Maturity-Wert des Users. Default in Phase 8 MVP: New. */
  maturity?: UserMaturity;
  /**
   * Aktuelle Berechtigungs-Konfig des Users. Wird gegen
   * `tour.requiredPermissions` geprueft — Tours, deren Permission-
   * Anforderung nicht erfuellt ist, sind nie offer- oder startbar.
   * Optional: wird aus Kompatibilitaet undefined akzeptiert; nicht-
   * gesetzte Permissions zaehlen dann als nicht-erfuellt fuer Tours
   * mit einer Permission-Anforderung (sicherer Default).
   */
  permissions?: UserPermissionConfig;
  state: TourStateSnapshot;
  trigger: TourTrigger;
  killSwitchActive: boolean;
  /** Aktuelle Route (urlAfterRedirects). Nur gesetzt, wenn Trigger = RouteChange. */
  route?: string;
  /** Name des Domain-Events. Nur gesetzt, wenn Trigger = AfterSuccess. */
  eventName?: string;
}

/**
 * Zentraler Regel-Dispatcher fuer die Frage: "darf die Tour jetzt
 * angeboten/gestartet werden?".
 *
 * Regel-Reihenfolge (erster Blocker gewinnt):
 *  1. Kill-Switch — keine Tours ueberhaupt
 *  2. Trigger-Match — Tour muss den gegebenen Trigger im `triggers`-Array haben
 *  3. Route-Match — bei RouteChange: `triggerContext.route` muss passen
 *  4. Event-Match — bei AfterSuccess: `triggerContext.requiresSuccessOf` muss
 *     dem gelieferten `eventName` entsprechen
 *  5. Audience-Match — aktuelle Audience muss zu Tour.audience passen
 *  6. Maturity-Match — wenn `tour.maturity` gesetzt, muss der aktuelle
 *     Maturity-Wert enthalten sein (leer = alle erlaubt)
 *  7. Status-Block — Completed (mit suppressAfterCompletion) oder State in
 *     aktivem Cooldown
 */
@Injectable({ providedIn: 'root' })
export class EligibilityService {
  canOffer(ctx: EligibilityContext): EligibilityResult {
    if (ctx.killSwitchActive) {
      return { eligible: false, reason: 'kill-switch' };
    }

    if (!ctx.tour.triggers.includes(ctx.trigger)) {
      return { eligible: false, reason: 'trigger-mismatch' };
    }

    // Route-Match: nur relevant fuer RouteChange-Trigger. Wenn die Tour einen
    // Route-Context hat, muss sie zur aktuellen URL passen (Prefix oder Regex).
    if (ctx.trigger === TourTrigger.RouteChange) {
      if (!this.routeMatches(ctx.tour, ctx.route)) {
        return { eligible: false, reason: 'route-mismatch' };
      }
    }

    // Event-Match: AfterSuccess-Tours muessen das erwartete Event nennen.
    // Wenn `requiresSuccessOf` nicht gesetzt ist, die Tour aber als
    // AfterSuccess registriert wurde, gilt sie als **nicht** zum aktuellen
    // Event passend — wir koennen sonst nicht unterscheiden, welches Event
    // sie triggern soll.
    if (ctx.trigger === TourTrigger.AfterSuccess) {
      const required = ctx.tour.triggerContext?.requiresSuccessOf;
      if (!required || required !== ctx.eventName) {
        return { eligible: false, reason: 'event-mismatch' };
      }
    }

    if (!this.audienceMatches(ctx.tour.audience, ctx.audience)) {
      return { eligible: false, reason: 'audience-mismatch' };
    }

    // Permission-Gate: Tours mit `requiredPermissions` werden hier vor allen
    // State-Pruefungen abgelehnt, wenn der User die noetigen Berechtigungen
    // nicht hat. Audience reicht hier nicht — ein TeamLeader, dem das
    // Edit-Recht auf Firmen-Einstellungen entzogen wurde, soll den
    // "Unternehmensdaten"-Schritt nicht als Nudge bekommen.
    if (!hasAllPermissions(ctx.permissions, ctx.tour.requiredPermissions)) {
      return { eligible: false, reason: 'permission-mismatch' };
    }

    if (!this.maturityMatches(ctx.tour.maturity, ctx.maturity)) {
      return { eligible: false, reason: 'maturity-mismatch' };
    }

    const now = new Date();

    // Offer-Cooldown (z. B. nach Nudge-Dismiss) aktiv?
    if (
      ctx.state.offerDismissedUntil &&
      ctx.state.offerDismissedUntil > now
    ) {
      return {
        eligible: false,
        reason: 'offer-cooldown',
        nextEligibleAt: ctx.state.offerDismissedUntil,
      };
    }

    // Snooze-Zeitraum laeuft noch?
    if (
      ctx.state.status === TourStateStatus.Snoozed &&
      ctx.state.snoozedUntil &&
      ctx.state.snoozedUntil > now
    ) {
      return {
        eligible: false,
        reason: 'snooze-active',
        nextEligibleAt: ctx.state.snoozedUntil,
      };
    }

    // AutoCompleted: Daten-Bedingung ist erfuellt, der User braucht den
    // Nudge nicht. Wenn er die Tour trotzdem ansehen will, kann er sie
    // ueber die Checklist-FAB starten ("Tour anschauen"-Button).
    if (ctx.state.status === TourStateStatus.AutoCompleted) {
      return { eligible: false, reason: 'state-block' };
    }

    // Started: Resume-Nudge ist erlaubt, aber nicht spammig. Wenn die
    // Tour innerhalb der letzten 10 Minuten schon ein Offer hatte, drosseln.
    // Verhindert das beobachtete 7×-Nudge-Verhalten zwischen 08:47 und 08:59
    // bei einer pausierten Tour.
    if (ctx.state.status === TourStateStatus.Started) {
      const RESUME_NUDGE_COOLDOWN_MS = 10 * 60 * 1000; // 10 Minuten
      const lastSeen = ctx.state.lastSeenAt;
      if (lastSeen && now.getTime() - lastSeen.getTime() < RESUME_NUDGE_COOLDOWN_MS) {
        return {
          eligible: false,
          reason: 'resume-nudge-cooldown',
          nextEligibleAt: new Date(
            lastSeen.getTime() + RESUME_NUDGE_COOLDOWN_MS
          ),
        };
      }
    }

    // Abgeschlossen und suppressAfterCompletion? → nicht erneut anbieten.
    if (
      ctx.state.status === TourStateStatus.Completed &&
      ctx.tour.statePolicy.suppressAfterCompletion
    ) {
      return { eligible: false, reason: 'state-block' };
    }

    // Dismiss-Cooldown: wenn Tour dismissed und innerhalb
    // cooldownHoursAfterDismiss, dann noch nicht wieder anbieten.
    if (ctx.state.status === TourStateStatus.Dismissed) {
      const cooldownMs =
        ctx.tour.statePolicy.cooldownHoursAfterDismiss * 3_600_000;
      const dismissedAt =
        ctx.state.dismissedAt ?? new Date(0);
      const nextEligibleAt = new Date(dismissedAt.getTime() + cooldownMs);
      if (nextEligibleAt > now) {
        return {
          eligible: false,
          reason: 'dismiss-cooldown',
          nextEligibleAt,
        };
      }
    }

    return { eligible: true };
  }

  canStart(ctx: EligibilityContext): EligibilityResult {
    // canStart ist identisch zu canOffer fuer MVP. Spaeter kann hier z. B.
    // `state-block` fuer Started weicher behandelt werden (Resume).
    return this.canOffer(ctx);
  }

  private audienceMatches(
    tourAudience: Audience[],
    currentAudience: Audience[]
  ): boolean {
    if (!tourAudience || tourAudience.length === 0) return true;
    return tourAudience.some((a) => currentAudience.includes(a));
  }

  /**
   * Maturity-Filter: leer / undefined im Tour-Config = alle Maturities
   * erlaubt. Andernfalls muss der aktuelle Wert explizit enthalten sein.
   * Wenn der Resolver noch keinen Wert hat (undefined), gilt das als
   * "passt nicht" — wir wollen keine Maturity-gefilterte Tour ausspielen,
   * wenn wir den User noch gar nicht klassifiziert haben.
   */
  private maturityMatches(
    tourMaturity: UserMaturity[] | undefined,
    current: UserMaturity | undefined
  ): boolean {
    if (!tourMaturity || tourMaturity.length === 0) return true;
    if (!current) return false;
    return tourMaturity.includes(current);
  }

  /**
   * Prefix-Match (String) oder Regex-Match (RegExp) gegen die aktuelle URL.
   * Wenn die Tour keinen Route-Context hat, gilt sie als **nicht** passend
   * fuer Route-Change-Evaluation — sonst wuerde jede Tour bei jeder
   * Navigation angeboten.
   */
  private routeMatches(tour: TourConfig, url: string | undefined): boolean {
    if (!url) return false;
    const r = tour.triggerContext?.route;
    if (!r) return false;
    if (typeof r === 'string') {
      // Exakter Treffer oder Prefix-mit-Slash — verhindert, dass
      // "/employee" auch "/employeeDetails" matcht (sauberer Cut).
      return url === r || url.startsWith(r + '/') || url.startsWith(r + '?');
    }
    return r.test(url);
  }
}
